import React, { useState } from "react";
import { ArrowRight, Phone, Mail } from "lucide-react";

interface FormData {
  name: string;
  mobileNumber: string;
  city: string;
  message: string;
}

interface ValidationErrors {
  name?: string;
  mobileNumber?: string;
  city?: string;
  message?: string;
}

const ServiceCTA = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    mobileNumber: "",
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

  // Client-side validation
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    } else if (formData.name.trim().length > 50) {
      errors.name = "Name cannot exceed 50 characters";
    } else if (!/^[a-zA-Z\s.'-]+$/.test(formData.name.trim())) {
      errors.name =
        "Name can only contain letters, spaces, dots, hyphens and apostrophes";
    }

    // Mobile number validation
    if (!formData.mobileNumber.trim()) {
      errors.mobileNumber = "Mobile number is required";
    } else {
      const cleanNumber = formData.mobileNumber.replace(/[\s\-()]/g, "");
      if (!/^[+]?[1-9]\d{9,14}$/.test(cleanNumber)) {
        errors.mobileNumber = "Please enter a valid mobile number";
      }
    }

    // City validation
    if (!formData.city.trim()) {
      errors.city = "City is required";
    } else if (formData.city.trim().length < 2) {
      errors.city = "City must be at least 2 characters";
    } else if (formData.city.trim().length > 30) {
      errors.city = "City cannot exceed 30 characters";
    } else if (!/^[a-zA-Z\s.'-]+$/.test(formData.city.trim())) {
      errors.city =
        "City can only contain letters, spaces, dots, hyphens and apostrophes";
    }

    if (!formData.message.trim()) {
      errors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      errors.message = "Message must be at least 10 characters";
    } else if (formData.message.trim().length > 500) {
      errors.message = "Message cannot exceed 500 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setValidationErrors({});

    // Client-side validation
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(
          result.message ||
            "Contact form submitted successfully! We will get back to you soon."
        );
        setFormData({ name: "", mobileNumber: "", city: "", message: "" });
      } else {
        // Handle validation errors from server
        if (result.errors && Array.isArray(result.errors)) {
          const serverErrors: ValidationErrors = {};
          result.errors.forEach((err: any) => {
            if (err.path) {
              serverErrors[err.path as keyof ValidationErrors] = err.msg;
            }
          });
          setValidationErrors(serverErrors);
        } else {
          setError(result.message || "Something went wrong. Please try again.");
        }
      }
    } catch (err) {
      console.error("Network error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-wine to-wine-dark text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <h2 className="text-4xl md:text-5xl font-display mb-6">
              Ready to Transform Your Look?
            </h2>
            <p className="text-xl text-white/90 mb-8 font-body">
              Book your consultation today and let our expert team create the
              perfect look for your special occasion. We're here to make you
              feel confident and beautiful.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center">
                <Phone className="w-5 h-5 mr-3 text-white/80" />
                <span className="font-body">+91 90769 22298</span>
              </div>
              <div className="flex items-center">
                <Mail className="w-5 h-5 mr-3 text-white/80" />
                <span className="font-body">hello@awmluxury.com</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="w-1/2 flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/919076922298?text=Hi%2C%20I%20have%20a%20query%20for%20AWM."
                target="_blank"
                rel="noopener noreferrer"
                className="w-full group bg-white text-wine px-8 py-2 rounded-lg font-medium hover:bg-beige transition-all duration-300 flex items-center justify-center font-body"
              >
                Queries
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Right Content - Contact Form */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
            <h3 className="text-2xl font-display mb-6 text-center">
              Quick Contact
            </h3>

            {/* Error Message */}
            {error && (
              <div
                className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded"
                role="alert"
              >
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div
                className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded"
                role="alert"
              >
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Name *"
                  required
                  aria-required="true"
                  aria-invalid={!!validationErrors.name}
                  aria-describedby={
                    validationErrors.name ? "name-error" : undefined
                  }
                  className={`w-full border rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 transition font-body ${
                    validationErrors.name
                      ? "border-red-400 focus:ring-red-400"
                      : "border-white/30 focus:ring-white/50 bg-white/20 text-white placeholder-white/70"
                  }`}
                />
                {validationErrors.name && (
                  <p
                    id="name-error"
                    className="mt-1 text-sm text-red-300 font-body"
                    role="alert"
                  >
                    {validationErrors.name}
                  </p>
                )}
              </div>

              <div>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  placeholder="Mobile Number *"
                  required
                  aria-required="true"
                  aria-invalid={!!validationErrors.mobileNumber}
                  aria-describedby={
                    validationErrors.mobileNumber ? "mobile-error" : undefined
                  }
                  className={`w-full border rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 transition ${
                    validationErrors.mobileNumber
                      ? "border-red-400 focus:ring-red-400"
                      : "border-white/30 focus:ring-white/50 bg-white/20 text-white placeholder-white/70 font-body"
                  }`}
                />
                {validationErrors.mobileNumber && (
                  <p
                    id="mobile-error"
                    className="mt-1 text-sm text-red-300 font-body"
                    role="alert"
                  >
                    {validationErrors.mobileNumber}
                  </p>
                )}
              </div>

              <div>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Your City *"
                  required
                  aria-required="true"
                  aria-invalid={!!validationErrors.city}
                  aria-describedby={
                    validationErrors.city ? "city-error" : undefined
                  }
                  className={`w-full border rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 transition ${
                    validationErrors.city
                      ? "border-red-400 focus:ring-red-400"
                      : "border-white/30 focus:ring-white/50 bg-white/20 text-white placeholder-white/70 font-body"
                  }`}
                />
                {validationErrors.city && (
                  <p
                    id="city-error"
                    className="mt-1 text-sm text-red-300 font-body"
                    role="alert"
                  >
                    {validationErrors.city}
                  </p>
                )}
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Message (minimum 10 characters) *"
                  rows={3}
                  required
                  aria-required="true"
                  aria-invalid={!!validationErrors.message}
                  aria-describedby={
                    validationErrors.message ? "message-error" : undefined
                  }
                  className={`w-full border rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 transition resize-vertical ${
                    validationErrors.message
                      ? "border-red-400 focus:ring-red-400"
                      : "border-white/30 focus:ring-white/50 bg-white/20 text-white placeholder-white/70 font-body"
                  }`}
                />
                {validationErrors.message && (
                  <p
                    id="message-error"
                    className="mt-1 text-sm text-red-300 font-body"
                    role="alert"
                  >
                    {validationErrors.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-white/70">
                  {formData.message.length}/500 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-wine py-2 rounded-md font-medium hover:bg-beige transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 font-body"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-wine"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  "Send Message"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceCTA;
